import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getWebhookLogs, replayWebhookLog } from '../../services/adminService';

interface WebhookLogItem {
  id: string;
  provider: string;
  endpoint: string;
  requestMethod: string;
  eventType: string | null;
  receiptNumber: string | null;
  productId: string | null;
  buyerEmail: string | null;
  buyerName: string | null;
  requestHeaders: Record<string, unknown> | null;
  payload: Record<string, unknown> | null;
  processingStatus: string;
  responseStatus: number | null;
  resultMessage: string | null;
  errorMessage: string | null;
  relatedUserId: string | null;
  createdAt: string;
}

const STATUS_BADGE_STYLES: Record<string, string> = {
  processed: 'bg-green-100 text-green-800',
  duplicate: 'bg-yellow-100 text-yellow-800',
  rejected: 'bg-red-100 text-red-800',
  error: 'bg-red-100 text-red-800',
  ignored: 'bg-gray-100 text-gray-800',
};

const WebhookLogs: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<WebhookLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replayingId, setReplayingId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    void load();
  }, [page, statusFilter]);

  const load = async (searchValue = search) => {
    try {
      setLoading(true);
      setError(null);
      setActionMessage(null);
      const data = await getWebhookLogs({
        page,
        limit,
        provider: 'jvzoo',
        status: statusFilter || undefined,
        search: searchValue || undefined,
      });
      setItems(data.logs || []);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to load webhook logs', err);
      setError('Failed to load webhook logs');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handleReplay = async (item: WebhookLogItem) => {
    try {
      setReplayingId(item.id);
      setActionMessage(null);
      const result = await replayWebhookLog(item.id);
      const replayBody = result?.replayBody || {};
      setActionMessage({
        type: 'success',
        text: `Replay finished with status ${result?.replayStatus}. ${replayBody.message || replayBody.error || 'Check the newest webhook log entry for details.'}`,
      });
      await load();
    } catch (err: any) {
      console.error('Failed to replay webhook log', err);
      setActionMessage({
        type: 'error',
        text: err?.response?.data?.error || 'Failed to replay webhook log',
      });
    } finally {
      setReplayingId(null);
    }
  };

  const renderDataBlock = (label: string, value: Record<string, unknown> | null) => (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <pre className="mt-2 overflow-x-auto rounded-md bg-gray-900 p-3 text-xs text-gray-100">
        {JSON.stringify(value || {}, null, 2)}
      </pre>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Webhook Logs</h1>
        <p className="text-sm text-gray-600">Every JVZoo webhook request received by the app, including rejected and failed ones.</p>
      </div>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              setPage(1);
              void load(event.currentTarget.value);
            }
          }}
          placeholder="Search by email, receipt, product ID, or event type"
          className="block w-full rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500 sm:max-w-md"
        />
        <button
          onClick={() => {
            setPage(1);
            void load();
          }}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          Search
        </button>
        <select
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPage(1);
          }}
          className="block rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500 sm:w-56"
        >
          <option value="">All statuses</option>
          <option value="processed">Processed</option>
          <option value="duplicate">Duplicate</option>
          <option value="rejected">Rejected</option>
          <option value="ignored">Ignored</option>
          <option value="error">Error</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      {actionMessage && (
        <div className={`mb-4 rounded border px-4 py-3 ${actionMessage.type === 'success' ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
          {actionMessage.text}
        </div>
      )}

      {loading ? (
        <div className="text-gray-500">Loading...</div>
      ) : (
        <div className="overflow-hidden rounded bg-white shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Buyer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Receipt</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Result</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 w-10">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {items.map((item) => {
                  const isExpanded = expandedId === item.id;
                  return (
                    <React.Fragment key={item.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {new Date(item.createdAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_STYLES[item.processingStatus] || 'bg-gray-100 text-gray-800'}`}>
                            {item.processingStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          <div className="font-medium text-gray-900">{item.eventType || 'unknown'}</div>
                          <div className="text-xs text-gray-500">{item.productId || 'no product'}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          <div>{item.buyerEmail || 'unknown'}</div>
                          <div className="text-xs text-gray-500">{item.buyerName || 'no name'}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {item.receiptNumber || 'n/a'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div>{item.resultMessage || 'No result message'}</div>
                          {item.errorMessage && (
                            <div className="mt-1 text-xs text-red-600">{item.errorMessage}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <button
                            onClick={() => void handleReplay(item)}
                            disabled={replayingId === item.id}
                            className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {replayingId === item.id ? 'Replaying...' : 'Replay'}
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : item.id)}
                            className="text-gray-400 hover:text-gray-600"
                            title={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="bg-gray-50 px-4 py-4">
                            <div className="grid gap-4 lg:grid-cols-2">
                              {renderDataBlock('Payload', item.payload)}
                              {renderDataBlock('Headers', item.requestHeaders)}
                            </div>
                            <div className="mt-4 grid gap-2 text-xs text-gray-600 sm:grid-cols-3">
                              <div><span className="font-semibold text-gray-900">Endpoint:</span> {item.endpoint}</div>
                              <div><span className="font-semibold text-gray-900">Method:</span> {item.requestMethod}</div>
                              <div><span className="font-semibold text-gray-900">Response:</span> {item.responseStatus ?? 'n/a'}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                      No webhook log entries found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
              <div className="text-sm text-gray-600">Total: {total}</div>
              <div className="space-x-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  className="rounded border border-gray-300 bg-white px-3 py-1 text-sm disabled:opacity-50"
                >
                  Prev
                </button>
                <span className="text-sm text-gray-700">Page {page} of {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  className="rounded border border-gray-300 bg-white px-3 py-1 text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookLogs;
